import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';

import { Image } from 'site-modules/shared/components/image/image';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';
import { getS3MediaDefaultImageUrlBySize } from 'client/utils/image-helpers';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';

import './car-image.scss';

const IMAGE_WIDTH = 300;
const IMAGE_HEIGHT = 200;

export function CarImageContent({
  href = null,
  sponsoredLogo = null,
  sponsoredLogoLink = null,
  makeSlug = null,
  modelSlug = null,
  year = null,
  submodelName = null,
  submodelSlug = null,
  showPhotoflipperButton = true,
  imageClassName = null,
  lazy = false,
}) {
  // href is null until its model value resolved, once resolved it's either empty string or string with photo url
  if (href === null) {
    return null;
  }

  let logo;
  if (sponsoredLogo) {
    const logoImg = (
      <Image className="pt-0_5 mw-100 d-block mx-auto" imageId={sponsoredLogo} alt="sponsor logo" source="static" />
    );

    if (sponsoredLogoLink) {
      logo = (
        <a href={sponsoredLogoLink} target="_blank" rel="noopener noreferrer">
          {logoImg}
        </a>
      );
    } else {
      logo = logoImg;
    }
  }

  const fallbackImage = getS3MediaDefaultImageUrlBySize(
    {
      make: { slug: makeSlug },
      model: { slug: modelSlug },
      year,
      submodels: { slug: submodelSlug },
    },
    IMAGE_WIDTH,
    IMAGE_HEIGHT
  );

  return (
    <div className="text-center comparator-car-image h-100">
      <div className="pos-r img-wrapper h-100">
        <Image
          className={classnames('core-img img-fluid d-block mx-auto', imageClassName)}
          imageId={href || fallbackImage}
          alt="Car"
          source="media"
          lazy={lazy}
        />
        {showPhotoflipperButton && makeSlug && modelSlug && year && (
          <PhotoflipperButtonAsync makeSlug={makeSlug} modelSlug={modelSlug} year={year} submodel={submodelName}>
            <button className="border-0 photoflipper-button pos-a left-0 right-0 top-0 bottom-0">
              <span className="text-gray-lighter rounded-bottom w-100 small text-start px-1 py-0_5 rounded-bottom pos-a left-0 bottom-0 text-shadow-contrast-high">
                <i className="icon-images" aria-hidden /> View all photos
              </span>
            </button>
          </PhotoflipperButtonAsync>
        )}
      </div>
      {logo}
    </div>
  );
}

CarImageContent.propTypes = {
  href: PropTypes.string,
  sponsoredLogo: PropTypes.string,
  sponsoredLogoLink: PropTypes.string,
  submodelName: PropTypes.string,
  submodelSlug: PropTypes.string,
  makeSlug: PropTypes.string,
  year: PropTypes.number,
  modelSlug: PropTypes.string,
  showPhotoflipperButton: PropTypes.bool,
  imageClassName: PropTypes.string,
  lazy: PropTypes.bool,
};

export const CarImageStateToProps = {
  href: bindToPath(
    ({
      makeSlug = null,
      modelSlug = null,
      year = null,
      submodelName = undefined,
      trimName = undefined,
      imageWidth = undefined,
    }) =>
      makeSlug &&
      buildMMYSubmodelTrimPhotosPathWithParams({
        make: makeSlug,
        model: modelSlug,
        submodel: submodelName,
        trim: trimName,
        year,
        width: imageWidth,
      }),
    PhotosModel,
    photos => get(photos, '[0].href', '')
  ),
};

export const CarImage = connectToModel(CarImageContent, CarImageStateToProps);

CarImage.propTypes = {
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  submodelName: PropTypes.string,
  trimName: PropTypes.string,
  year: PropTypes.number,
  sponsoredLogo: PropTypes.string,
  showPhotoflipperButton: PropTypes.bool,
  lazy: PropTypes.bool,
  imageWidth: PropTypes.string,
};
